import { isEmpty } from 'lodash';
import { AccountMeta, RawAccountInitialData } from 'api/account';
import { Project } from 'api/project';
import { ResourceManagementConfig } from 'api/resource-management/types';
import { Field } from 'api/tasks';
import { ApiConfiguration } from 'api/timesheets/configuration/api-configuration';
import { API_HOST } from 'config/environment';
import { AccountFields } from 'data/fields/hooks';
import { MembersResponse, MembersV2Response } from 'data/members/api';
import { CustomerPreview } from 'services';
import { TimerApi } from 'services/timer';

export enum InitWorkerRequestType {
  GetMeta = 'GET_META',
  Init = 'INIT',
  ProjectsLight = 'PROJECTS_LIGHT',
  TaskFields = 'TASK_FIELDS',
  Fields = 'FIELDS',
  Members = 'MEMBERS',
  RmConfig = 'RM_CONFIG',
  TimesheetConifg = 'TS_CONFIG',
  ProjectPageMembers = 'PROJECT_PAGE_MEMBERS',
}

type TypeName =
  | InitWorkerRequestType.GetMeta
  | InitWorkerRequestType.Init
  | InitWorkerRequestType.ProjectsLight
  | InitWorkerRequestType.TaskFields
  | InitWorkerRequestType.Fields
  | InitWorkerRequestType.Members
  | InitWorkerRequestType.TimesheetConifg
  | InitWorkerRequestType.RmConfig
  | InitWorkerRequestType.ProjectPageMembers;

type ObjectType<T> = T extends 'TS_CONFIG'
  ? ApiConfiguration
  : T extends 'GET_META'
  ? AccountMeta
  : T extends 'INIT'
  ? RawAccountInitialData
  : T extends 'PROJECTS_LIGHT'
  ? Project[]
  : T extends 'TASK_FIELDS'
  ? AccountFields
  : T extends 'FIELDS'
  ? Field[]
  : T extends 'MEMBERS'
  ? MembersV2Response
  : T extends 'RM_CONFIG'
  ? ResourceManagementConfig
  : T extends 'PROJECT_PAGE_MEMBERS'
  ? MembersResponse
  : never;

// this is used so that when refetching data from the api, it should return new values
const invalidateKey = {
  [InitWorkerRequestType.Fields]: false,
  [InitWorkerRequestType.TaskFields]: false,
  [InitWorkerRequestType.GetMeta]: false,
  [InitWorkerRequestType.Init]: false,
  [InitWorkerRequestType.Members]: false,
  [InitWorkerRequestType.ProjectPageMembers]: false,
  [InitWorkerRequestType.RmConfig]: false,
  [InitWorkerRequestType.TimesheetConifg]: false,
  [InitWorkerRequestType.ProjectsLight]: false,
};

const getData = async <T extends TypeName>(key: TypeName) => {
  const [isPreview] = await CustomerPreview.isPreviewMode();

  return new Promise<ObjectType<T>>((resolve, rej) => {
    const isInvalidate = invalidateKey[key];
    const timer = new TimerApi(30000);

    if (window.initWorker == null) {
      rej();
    }

    window.initWorker.postMessage({
      type: 'getData',
      request: key,
      isInvalidate,
      isCustomerPreview:
        window.location.search.includes('customer-preview=true') || isPreview,
      data: {
        apiKey:
          localStorage.getItem('__api_key') != null
            ? JSON.parse(localStorage.getItem('__api_key') as string)[1]
            : undefined,
        domain: `${API_HOST}/api`,
      },
    });
    timer.start();
    timer.onElapsed(() => {
      rej();
    });
    window.initWorker.addEventListener('message', (e) => {
      if (e.data.type === key && e.data.respData === 'ERROR_OCCURRED') {
        timer.stop();
        rej();
      } else if (e.data.type === key && !isEmpty(e.data.respData)) {
        timer.stop();
        resolve(JSON.parse(e.data.respData));
        invalidateKey[key] = true;
      }
    });
  });
};

const setData = async () => {
  if (window.initWorker == null) {
    return;
  }
  const isApiKeyPresent = localStorage.getItem('__api_key') != null;

  const [isPreview] = await CustomerPreview.isPreviewMode();

  window.initWorker.addEventListener('message', () => {
    window.initWorker.postMessage({
      type: isApiKeyPresent ? 'setData' : 'setMetaData',
      isInvalidate: false,
      isCustomerPreview:
        window.location.search.includes('customer-preview=true') || isPreview,
      data: {
        apiKey: isApiKeyPresent
          ? JSON.parse(localStorage.getItem('__api_key') as string)[1]
          : undefined,
        domain: `${API_HOST}/api`,
      },
    });
  });
};

export const InitWorkerApi = {
  getData,
  setData,
};
